import React, { useState } from 'react';
import { ArrowLeft, Mail, Phone, MapPin, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! Your message has been sent successfully.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          className="mb-6 text-[#2C2C2C] hover:text-[#C4B5A0]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-seasons font-light text-[#2C2C2C] mb-4">
              Contact Us
            </h1>
            <p className="text-[#8B7D6B] font-montserrat">
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="bg-white border-none shadow-lg p-8">
              <h2 className="text-2xl font-seasons text-[#2C2C2C] mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#F5F3F0] border-none focus:ring-2 focus:ring-[#C4B5A0]"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#F5F3F0] border-none focus:ring-2 focus:ring-[#C4B5A0]"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-[#F5F3F0] border-none focus:ring-2 focus:ring-[#C4B5A0]"
                    placeholder="+91 9876543210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-montserrat text-[#6B4E37] mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-[#F5F3F0] border-none focus:ring-2 focus:ring-[#C4B5A0] resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase py-6 transition-all duration-300 hover:shadow-lg"
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="bg-white border-none shadow-lg p-8">
                <h2 className="text-2xl font-seasons text-[#2C2C2C] mb-6">Get in Touch</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F3F0] p-3 rounded-full">
                      <MapPin className="h-5 w-5 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-1">Visit Us</h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">
                        52, Krishna Cloth Market<br />
                        Chandni Chowk, Delhi - 110006
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F3F0] p-3 rounded-full">
                      <Phone className="h-5 w-5 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-1">Call Us</h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">+91 9811193388</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F3F0] p-3 rounded-full">
                      <Mail className="h-5 w-5 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-1">Email Us</h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">ssons1975@gmail.com</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#F5F3F0] border-none p-6">
                <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-3">Business Hours</h3>
                <div className="space-y-2 text-sm text-[#2C2C2C] font-montserrat">
                  <p><strong>Monday - Saturday:</strong> 11:30 AM - 8:00 PM</p>
                  <p><strong>Sunday:</strong> CLOSED</p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};